'use client';

import * as React from 'react';
import { LuChevronDown, LuPlus, LuPencil, LuTrash2, LuX, LuCheck } from 'react-icons/lu';

import { Button } from '@core/ui/primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuShortcut,
} from '@core/ui/primitives/dropdown-menu';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@core/ui/primitives/input-group';
import { Kbd } from '@core/ui/primitives/kbd';
import { getSwitcherModifierLabel, isSwitcherShortcut, SWITCHER_HOTKEY } from './shortcuts';
import { cn } from '@core/helpers/misc/GeneralUtils';
import type { Mindmap } from '@/features/mindmap/core/MindmapTypes';
import {
  ensureIndex,
  listMindmaps,
  getActiveMindmapId,
  setActiveMindmapId,
  createNewMindmap,
  renameMindmap,
  deleteMindmap,
} from '@/features/mindmap/core/MindmapMulti';

export interface MindmapSwitcherProps {
  className?: string;
  onSwitch?: (mindmap: Mindmap) => void;
  onOpenChange?: (open: boolean) => void;
}

export function MindmapSwitcher({ className, onSwitch, onOpenChange }: MindmapSwitcherProps) {
  const [open, setOpen] = React.useState(false);
  const [itemsVersion, setItemsVersion] = React.useState(0);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [titleInput, setTitleInput] = React.useState('');
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const firstItemRef = React.useRef<HTMLDivElement | null>(null);
  const renameInputRef = React.useRef<HTMLInputElement | null>(null);
  const [renaming, setRenaming] = React.useState(false);
  const [confirmDeleteActive, setConfirmDeleteActive] = React.useState(false);

  const setMenuOpen = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  const refresh = React.useCallback(() => {
    try {
      ensureIndex();
      const idx = listMindmaps();
      setActiveId(idx.activeId);
      const t = idx.items.find((i) => i.id === idx.activeId)?.title ?? 'Mindmap';
      setTitleInput(t);
      setItemsVersion((v) => v + 1);
    } catch {}
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Resolve current index (list of mindmaps) early so hooks below can depend on it safely
  const idx = React.useMemo(() => {
    try {
      return listMindmaps();
    } catch {
      return { version: 1, activeId: null, items: [] } as const;
    }
  }, [itemsVersion]);

  // Global shortcut: toggle switcher via configured hotkey; focus first item on open
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isSwitcherShortcut(e)) return;
      e.preventDefault();
      const nextState = !open;
      setMenuOpen(nextState);
      if (nextState) {
        setTimeout(() => {
          if (firstItemRef.current) {
            firstItemRef.current.focus();
          } else {
            renameInputRef.current?.focus();
          }
        }, 10);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, setMenuOpen]);

  // Close on Escape explicitly (Radix also handles this, but ensure consistency)
  React.useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) setMenuOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, setMenuOpen]);

  // (defined after handleSelect to avoid TDZ on some bundlers)

  const activeTitle = React.useMemo(() => {
    return idx.items.find((i) => i.id === activeId)?.title ?? 'Mindmap';
  }, [idx.items, activeId]);

  const handleSelect = React.useCallback(
    (id: string) => {
      const next = setActiveMindmapId(id);
      if (next && onSwitch) onSwitch(next);
      setMenuOpen(false);
      refresh();
    },
    [onSwitch, refresh, setMenuOpen]
  );

  // While menu is open, swallow navigation/confirm keys at capture phase so they don't reach canvas handlers
  const handleMenuKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.key;
      // Always stop canvas from seeing these
      if (key === 'Enter' || key === ' ' || key === 'Tab' || key.startsWith('Arrow') || key === 'F2') {
        event.stopPropagation();
      }
      // Hotkeys inside the menu (do not use F2 here)
      if (renaming) return;
      const lower = key.toLowerCase();
      // R -> rename
      if (lower === 'r') {
        event.preventDefault();
        setRenaming(true);
        setTimeout(() => renameInputRef.current?.focus(), 0);
        return;
      }
      // N -> new map
      if (lower === 'n') {
        event.preventDefault();
        handleCreate();
        return;
      }
      // D -> delete (toggle confirm on the delete item)
      if (lower === 'd') {
        event.preventDefault();
        setConfirmDeleteActive((v) => !v);
        return;
      }
      // 1..9 -> select corresponding map
      if (/^[1-9]$/.test(lower)) {
        event.preventDefault();
        const index = parseInt(lower, 10) - 1;
        const item = idx.items[index];
        if (item) handleSelect(item.id);
        return;
      }
    },
    [idx.items, renaming, handleSelect]
  );

  const handleCreate = React.useCallback(() => {
    const next = createNewMindmap('Untitled');
    if (onSwitch) onSwitch(next);
    setMenuOpen(false);
    refresh();
  }, [onSwitch, refresh, setMenuOpen]);

  const handleDelete = React.useCallback(
    (id: string) => {
      const next = deleteMindmap(id);
      setConfirmDeleteActive(false);
      refresh();
      if (next && onSwitch) onSwitch(next);
    },
    [onSwitch, refresh]
  );

  const handleRename = React.useCallback(
    (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      const id = getActiveMindmapId();
      if (!id) return;
      const trimmed = titleInput.trim();
      if (!trimmed) return;
      const next = renameMindmap(id, trimmed);
      if (next && onSwitch) onSwitch(next);
      refresh();
    },
    [onSwitch, titleInput, refresh]
  );

  return (
    <div className={cn('pointer-events-auto', className)}>
      <DropdownMenu
        open={open}
        onOpenChange={(o) => {
          setMenuOpen(o);
          if (!o) {
            setRenaming(false);
            setConfirmDeleteActive(false);
            // Drop focus from the trigger to avoid any focus ring/border
            try {
              triggerRef.current?.blur();
            } catch {}
            return;
          }
          // When opening, ensure rename is off and first item is focusable
          setRenaming(false);
          // Always reset delete confirmation when opening as well
          setConfirmDeleteActive(false);
          setTimeout(() => {
            if (firstItemRef.current) firstItemRef.current.focus();
          }, 10);
        }}>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            data-testid='mindmap-switcher-trigger'
            variant='outline'
            size='sm'
            className='h-8 gap-1 px-2 hover:bg-muted hover:text-foreground focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'>
            <span className='truncate max-w-[160px]' title={activeTitle}>
              {activeTitle}
            </span>
            <span className='hidden sm:inline-flex items-center gap-1 text-muted-foreground pl-1'>
              <Kbd size='sm' keys={[getSwitcherModifierLabel()]} />
              <Kbd size='sm'>{SWITCHER_HOTKEY.key.toUpperCase()}</Kbd>
            </span>
            <LuChevronDown className='h-4 w-4 opacity-70' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          loop
          ref={contentRef as any}
          align='start'
          sideOffset={8}
          className='w-[260px]'
          onKeyDown={handleMenuKeyDown}>
          <DropdownMenuLabel>Mindmaps</DropdownMenuLabel>
          {idx.items.map((item, index) => (
            <DropdownMenuItem
              key={item.id}
              ref={index === 0 ? (firstItemRef as any) : undefined}
              onSelect={() => handleSelect(item.id)}
              data-testid='mindmap-switcher-item'
              data-map-id={item.id}
              className='group flex items-center justify-between gap-2'>
              <span className='flex items-center gap-2 min-w-0'>
                <span
                  className={cn('inline-block h-2 w-2 rounded-full', item.id === activeId ? 'bg-primary' : 'bg-muted')}
                />
                <span className='truncate'>{item.title}</span>
              </span>
              {/* right-aligned numeric shortcut */}
              <DropdownMenuShortcut>{String(index + 1)}</DropdownMenuShortcut>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {/* New map (first) */}
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              handleCreate();
            }}
            className='flex items-center gap-2'>
            <LuPlus className='h-4 w-4' />
            New mindmap
            <DropdownMenuShortcut>N</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Active mindmap</DropdownMenuLabel>
          {/* Rename (second) */}
          {!renaming && (
            <DropdownMenuItem
              onSelect={(e) => {
                // Keep menu open; toggle to input mode
                e.preventDefault();
                setRenaming(true);
                setTimeout(() => {
                  if (renameInputRef.current) {
                    renameInputRef.current.focus();
                    renameInputRef.current.select();
                  }
                }, 0);
              }}
              className='flex items-center gap-2'>
              <LuPencil className='h-4 w-4' />
              Rename
              <DropdownMenuShortcut>R</DropdownMenuShortcut>
            </DropdownMenuItem>
          )}
          {renaming && (
            <div className='px-1 py-1.5'>
              <form
                onSubmit={(e) => {
                  handleRename(e);
                  // Return to normal view after commit
                  setRenaming(false);
                  // Keep focus inside the menu
                  setTimeout(() => firstItemRef.current?.focus(), 0);
                }}>
                <InputGroup className='h-8 px-2'>
                  <InputGroupAddon className='px-0.5 text-muted-foreground'>
                    <LuPencil className='h-3.5 w-3.5' />
                  </InputGroupAddon>
                  <InputGroupInput
                    ref={renameInputRef}
                    value={titleInput}
                    onChange={(e) => setTitleInput(e.currentTarget.value)}
                    onKeyDownCapture={(e) => {
                      // Prevent Radix DropdownMenu typeahead/roving-focus behavior
                      e.stopPropagation();
                    }}
                    onFocus={(e) => {
                      // Select all for quick overwrite
                      requestAnimationFrame(() => {
                        try {
                          const el = e.currentTarget;
                          el.setSelectionRange(0, el.value.length);
                        } catch {}
                      });
                    }}
                    placeholder='Rename map'
                    aria-label='Rename current mindmap'
                  />
                </InputGroup>
              </form>
            </div>
          )}
          {/* Delete (third) with inline confirmation */}
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setConfirmDeleteActive((v) => !v);
            }}
            className='flex items-center justify-between gap-2'>
            <span className='flex items-center gap-2'>
              <LuTrash2 className='h-4 w-4' />
              Delete
            </span>
            {!confirmDeleteActive ? (
              <DropdownMenuShortcut>D</DropdownMenuShortcut>
            ) : (
              <span className='flex items-center gap-2'>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  title='Cancel delete'
                  aria-label='Cancel delete'
                  className='p-1 rounded hover:bg-muted/80'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setConfirmDeleteActive(false);
                  }}>
                  <LuX className='h-3.5 w-3.5 text-red-600' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  title='Confirm delete'
                  aria-label='Confirm delete'
                  className='p-1 rounded hover:bg-muted/80'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const id = getActiveMindmapId();
                    if (id) handleDelete(id);
                    setConfirmDeleteActive(false);
                  }}>
                  <LuCheck className='h-3.5 w-3.5 text-green-600' />
                </Button>
              </span>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default MindmapSwitcher;
